import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { ProjectDataCacheService } from 'src/app/project-data-cache.service';
import { DialogService } from '../dialogservice.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ProjectsService } from '../utils/services/projects.service';
import { AgentsService } from '../utils/services/agents.service';
import { AgentsSearchService } from '../utils/services/agents-search.service';
import { KeycloakService } from 'keycloak-angular';
import { BookableTimeSlotsService } from '../utils/services/bookable-time-slots.service';
import { SharedService } from '../utils/services/shared.service';
import { Agent, AgentStatus } from '../utils/models/agent.model';
import { RecordingService } from '../utils/services/recording.service';
import { debounceTime, forkJoin, Subscription } from 'rxjs';
import moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Socket } from 'ngx-socket-io';
import { PartnersService } from '../utils/services/partners.service';
import { AgentviewComponent } from '../agentview/agentview.component';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { CreateAgentDialogComponent } from './createAgentDialog/createAgentDialog.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { NewProjectComponent } from '../newProject/newProject.component';
import { DragAndDropCardListComponent } from './drag-and-drop-card-list/drag-and-drop-card-list.component';
import { TimeSlotsDialogComponent } from '../timeSlotsDialog/timeSlotsDialog.component';
import { WhatsAppMessagesService } from '../utils/services/whats-app-messages.service';
import { MessageService } from '../utils/services/message.service';
import { MaterialModule } from '../material.module';
import { PreserveTimePipe } from '../utils/pipes/preserve-time.pipe';

interface AgentEntry {
  id: number
  name: string
  status: string
  previousStatus?: string
  profileImage?: string | null
  city?: string
  country?: string
  interviewAppointment?: Date
  assessmentAppointment?: Date
  public_id?: string
  salutation?: string
  agentProfilePhoto?: any
  videoFiles?: any[]
  voiceFiles?: any[]
  createdAt?: string | Date
  updatedAt?: string | Date
}

interface KanbanStatusColumn {
  name: string
  display: string
  customerDisplayName?: string
  roles: string[]
  agents: AgentEntry[]
  collapsed: boolean
}

@Component({
  selector: 'app-singleProjectView',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    DropDownListModule,
    SkeletonModule,
    DragAndDropCardListComponent,
    NewProjectComponent,
    CreateAgentDialogComponent,
    AgentviewComponent,
    TimeSlotsDialogComponent,
    SpinnerComponent,
    PreserveTimePipe,
  ],
  templateUrl: './singleProjectView.component.html',
  styleUrl: './singleProjectView.component.scss',
})
export class SingleProjectViewComponent {
  /**
   * Sorts the `agents` array of every status object by the timestamp when the
   * agent entered that status. The older an agent has been in a status, the
   * higher (further up) they will appear in the column.  
   *
   * The algorithm uses `updatedAt` as primary source because this timestamp is
   * updated each time the backend changes an agent (including status
   * transitions). Should this field be undefined it falls back to `createdAt`.
   */
  private sortAgentsInAllStatuses (): void {
    this.statuses.forEach(status => {
      status.agents.sort((agentA: any, agentB: any) => {
        const aTime = new Date(agentA.updatedAt ?? agentA.createdAt ?? 0).getTime()
        const bTime = new Date(agentB.updatedAt ?? agentB.createdAt ?? 0).getTime()
        return aTime - bTime // older (smaller timestamp) first
      })
    })

    this.userStatus.forEach(status => {
      status.agents.sort((agentA: any, agentB: any) => {
        const aTime = new Date(agentA.updatedAt ?? agentA.createdAt ?? 0).getTime()
        const bTime = new Date(agentB.updatedAt ?? agentB.createdAt ?? 0).getTime()
        return aTime - bTime // older (smaller timestamp) first
      })
    })
  }

  @Input()
  public prefix: string = '';

  //@Input()
  public project: any = null;

  // get id from route
  public id: number = -1;

  public agentId = -1;
  public isModal = true;
  public isMediaPlay: boolean = false;

  public unreadWhatsAppMessagesCount: Map<number, number> = new Map<number, number>();
  public unreadInternMessagesCount: Map<number, number> = new Map<number, number>();
  public readWhatsAppMessagesCountSub?: Subscription;
  public unreadWhatsAppMessagesCountSub?: Subscription;
  public unreadInternMessagesCountSub?: Subscription;
  private lastOpenedAgentId: number | null = null;
  
  // Speichert die originalen Agenten für die Suchfunktion
  private originalAgents: any[][] | null = null;

  // Speichert gefundene archivierte Agenten für die Anzeige neben dem Suchfeld
  public foundArchivedAgents: any[] = [];

  // Archivierte Agenten (werden bereits vom System bereitgestellt)
  private archivedAgents: any[] = [];

  search = new FormControl('');

  public statuses: KanbanStatusColumn[] = [
    {
      name: 'AgentCreatedFilesMissing',
      display: 'Neuer Agent',
      roles: ['Partner'],
      agents: [
        {
          id: 0,
          name: 'Agent anlegen',
          status: 'AgentCreatedFilesMissing',
          voiceFiles: [],
          videoFiles: [],
          previousStatus: 'AgentCreatedFilesMissing',
        },
      ],
      collapsed: false,
    },
    {
      name: 'ApplicantGetsChecked',
      display: 'In Prüfung',
      roles: ['Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'WaitingForAgent',
      display: 'Warte auf Agent / voicefile',
      roles: ['Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'ApplicantGetsSuggested',
      display: 'Dem Kunden vorgeschlagen',
      customerDisplayName: 'Neue Bewerber',
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'ApplicantRejected',
      display: 'Negative Auswahl',
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'AppointmentScheduled',
      display: 'Termin gewünscht / vereinbart',
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'Assessment',
      display: 'Assessment',
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'ApplicantAccepted',
      display: 'Zusage Auftraggeber', // Ehemals "positive Auswahl"
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
    {
      name: 'ApplicantConfirmed',
      display: 'Zusage Agent',
      roles: ['Client', 'Partner'],
      agents: [],
      collapsed: false,
    },
  ];

  public currentRole: string = '';
  public isByPartner: string = 'false';
  public partnerId: string = '';
  public partnerName: string = '';
  public data: any = {};
  public scheduledCount: number = 0;
  public currentUser: string = '';
  public currentUserName: string = '';
  public terminData: any;
  public bookedTimeSlots: any;
  public selectedVideoUrl: any;
  public selectedAudioUrl: any;
  public isAdminLoggedIn = false;
  public agents: Agent[] = [];
  public userStatus: KanbanStatusColumn[] = [];
  subscription: Subscription = new Subscription();
  isSpinnerActive: boolean = false;
  isProjectDataFetched: boolean = false;

  public constructor(
    public readonly projectDataCacheService: ProjectDataCacheService,
    public dialogService: DialogService,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private router: Router,
    private agentsService: AgentsService,
    private readonly agentsSearchService: AgentsSearchService,
    private keyCloak: KeycloakService,
    private btsService: BookableTimeSlotsService,
    private sharedService: SharedService,
    private recordingService: RecordingService,
    private snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private socket: Socket,
    public readonly partnersService: PartnersService,
    private readonly whatsAppMessagesService: WhatsAppMessagesService,
    private readonly messageService: MessageService
  ) {

    this.id = parseInt(this.route.snapshot.paramMap.get('id') ?? '-1')

    if (this.id) this.loadProjectData()

    this.agentVoiceFileStatusChange()
  }

  /**
   * Lade die Projektdaten und prüfe, ob die Assessment-Spalte angezeigt werden soll.
   * Wenn bei der Projektkonfiguration hasAssessment = false gesetzt wurde,
   * wird die Assessment-Spalte aus dem Kanban-Board entfernt.
   * Dies ermöglicht eine projektspezifische Anpassung des Workflows.
   */
  private loadProjectData() {
    
    this.projectsService.getProjectById(this.id).subscribe({

      next: (project) => {

        this.project = project
        
        if (this.currentRole === 'Partner' && this.partnerId) {
          console.log('loadProjectData: loading partner info for project', {
            partnerId: this.partnerId,
            projectId: this.id
          })
          this.getPartnerInfo(this.partnerId)
        }
        
        // Prüfe, ob Assessment-Spalte vorhanden ist
        const hasAssessmentColumn = this.statuses.some(status => status.name === 'Assessment');
        
        // Entferne oder füge Assessment-Spalte hinzu, je nach Projekteinstellung
        if (this.project.hasAssessment === false || this.project.hasAssessment === "false") {
          // Entferne Assessment-Spalte, wenn nicht aktiviert und noch vorhanden
          if (hasAssessmentColumn) {
            this.statuses = this.statuses.filter((status: any) => status.name !== 'Assessment');
            this.userStatus = this.userStatus.filter((status: any) => status.name !== 'Assessment');
          }
        } else {
          // Füge Assessment-Spalte hinzu, wenn aktiviert und noch nicht vorhanden
          if (!hasAssessmentColumn) {
            // Finde Position, wo Assessment eingefügt werden soll (vor ApplicantAccepted)
            const insertIndex = this.statuses.findIndex(status => status.name === 'ApplicantAccepted');
            if (insertIndex !== -1) {
              // Erstelle Assessment-Status
              const assessmentStatus = {
                name: 'Assessment',
                display: 'Assessment',
                roles: ['Client', 'Partner'],
                agents: [],
                collapsed: false
              };
              
              // Füge Assessment-Status an der richtigen Position ein
              this.statuses.splice(insertIndex, 0, assessmentStatus);
              this.userStatus.splice(insertIndex, 0, assessmentStatus);
            }
          }
        }
        
        // Sort agents before UI update
        this.sortAgentsInAllStatuses();
        // Erzwinge Change Detection, um die UI zu aktualisieren
        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {

        if (err.error.error == 'Not Found') this.router.navigateByUrl('/')
      },
    })
  }

  public ngOnInit(): void {
    this.isSpinnerActive = true;
    const tokenData = this.keyCloak.getKeycloakInstance().tokenParsed
    this.currentUser = tokenData?.['email']
    this.partnerId = tokenData?.['sub'] || ''
    // Extrahiere den Namen des Benutzers aus dem Token (given_name + family_name oder preferred_username als Fallback)
    const firstName = tokenData?.['given_name'] || ''
    const lastName = tokenData?.['family_name'] || ''
    this.currentUserName = firstName && lastName ? `${firstName} ${lastName}` : tokenData?.['preferred_username'] || this.currentUser
    const userRoles = tokenData?.realm_access?.roles;

    if (userRoles?.includes('Client')) {
      this.currentRole = 'Client';
      this.partnerId = '';
    } else if (userRoles?.includes('Partner')) {
      this.currentRole = 'Partner';
      this.isByPartner = 'true';
    } else if (userRoles?.includes('Admin')) {
      this.currentRole = 'Admin';
      this.partnerId = '';
      this.isAdminLoggedIn = true;
    }

    if (this.currentRole !== 'Admin') {

      this.statuses = this.statuses.filter((status: any) => status.roles.includes(this.currentRole))
    }
    this.getUserStatusByRole();

    this.sharedService.onFileAccepted.subscribe((res) => {
      this.onFileaccepted(res);
    });
    this.sharedService.onFileRejected.subscribe((res) => {
      this.onFilerejected(res);
    });

    this.subscription =
      this.sharedService.updatescheduledCountSubject.subscribe((value) => {
        this.updatescheduledCount();
      });

    this.route.paramMap.subscribe((params) => {
      this.id = parseInt(this.route.snapshot.paramMap.get('id') ?? '-1');
      if (!this.id) {
        this.router.navigateByUrl('/');
        return;
      }
      
      // Query-Parameter für die automatische Öffnung eines Agenten auslesen
      this.route.queryParamMap.subscribe(queryParams => {
        const agentId = queryParams.get('agentId');
        const status = queryParams.get('status');
        
        if (agentId) {
          // Timer setzen, um sicherzustellen, dass die Daten geladen sind
          setTimeout(() => {
            this.navigateToSingleView(parseInt(agentId), status);
          }, 1000);
        }
      });
  
      this.projectsService.getProjectById(this.id).subscribe({

        next: (project) => {

          this.project = project
          // Entferne Assessment-Spalte, wenn im Projekt nicht aktiviert
          if (this.project && this.project.hasAssessment === false) {
          
            this.statuses = this.statuses.filter((status: any) => status.name !== 'Assessment')
          }
        },
        error: (err) => {
          if (err.error.error === 'Not Found') {
            this.router.navigateByUrl('/');
          }
        },
      });

      this.agentVoiceFileStatusChange();
  
      this.partnersService.fetchAll(() => {
        this.partnerName =
          this.partnersService.partners.find(
            (partner) => partner.userId === this.partnerId
          )?.partnerName ?? '';
        this.getProjectDataConcurrent();
      });
    });
    this.isSpinnerActive = false;

    this.search.valueChanges.pipe(debounceTime(300)).subscribe((newValue: string | null) => this.filterAgentsByValue(newValue));
  }

  /**
   * Lädt Profilbilder für archivierte Agenten
   * Diese Methode wird aufgerufen, wenn archivierte Agenten in den Suchergebnissen angezeigt werden
   */
  private loadProfileImagesForArchivedAgents(): void {
    if (!this.foundArchivedAgents || this.foundArchivedAgents.length === 0) return;
    
    this.foundArchivedAgents.forEach(agent => {
      if (agent.agentProfilePhoto?.id) {
        this.agentsService.getAgentProfilePhoto(agent.agentProfilePhoto.id).subscribe({
          next: (photo: any) => {
            agent.profileImage = this.agentsService.convertImagetoUrl(photo.photoBlob);
            this.changeDetectorRef.detectChanges();
          },
          error: (err) => {
            console.error(`Failed to load photo for archived agent ${agent.id}`, err);
          }
        });
      }
    });
  }

  public filterAgentsByValue(newValue: string | null): void {
    // Wenn kein Suchwert oder leerer String, zeige alle Agenten
    if (!newValue || newValue.trim() === '') {
      // Wenn wir originale Agenten gespeichert haben, stelle sie wieder her
      if (this.originalAgents) {
        this.userStatus.forEach((status, index) => {
          // Stelle die originalen Agenten wieder her, aber behalte den "Agent anlegen" Eintrag
          if (status.name === 'AgentCreatedFilesMissing' && status.agents.length > 0 && status.agents[0].id === 0) {
            const createAgentEntry = status.agents[0]
            status.agents = [...this.originalAgents![index]]
            // Füge "Agent anlegen" wieder hinzu, falls er nicht bereits vorhanden ist
            if (status.agents.length === 0 || status.agents[0].id !== 0) {
              status.agents.unshift(createAgentEntry as never)
            }
          } else {
            status.agents = [...this.originalAgents![index]]
          }
        })
      }
      // Leere die Liste der archivierten Agenten
      this.foundArchivedAgents = []
      return
    }

    if (!this.originalAgents) {
      this.originalAgents = this.userStatus.map(status => [...status.agents])
    }

    // Filtere Agenten in jeder Status-Kategorie nur nach Vor- und Nachnamen
    this.userStatus.forEach((status, index) => {
      const originalAgentsForStatus = [...this.originalAgents![index]]
      const createEntry = originalAgentsForStatus.find(a => a.id === 0)
      const withoutCreate = originalAgentsForStatus.filter(a => a.id !== 0)
      const filtered = this.agentsSearchService.filterAgentsByNameOnly(withoutCreate, newValue)
      status.agents = createEntry && status.name === 'AgentCreatedFilesMissing'
        ? [createEntry, ...filtered]
        : [...filtered]
    })

    const activeMatchFound = this.userStatus.some(status => status.agents.some(agent => agent.id !== 0))

    // Fallback: Suche auch in ALLEN Agenten (nicht nur Archiv), wenn im Kanban keine Treffer vorhanden sind
    if (this.currentRole === 'Partner' || this.currentRole === 'Admin') {
      if (!activeMatchFound) {
        const partnerIdParam = this.currentRole === 'Partner' ? this.partnerId : ''
        this.agentsService.getAllAgentsByUserRole(partnerIdParam)
          .subscribe((allAgents: any[]) => {
            const globalMatches = this.agentsSearchService.filterAgentsByNameOnly(allAgents, newValue)
            this.foundArchivedAgents = globalMatches
            this.loadProfileImagesForArchivedAgents()
          })
      } else {
        this.foundArchivedAgents = []
      }
    }
  }

  private getProjectDataConcurrent(): void {
    if (this.currentRole === 'Partner' && !this.partnerName) return;

    forkJoin({
      agents: this.agentsService.getAgentsByProjectIdAndPartnerName(this.id, this.partnerName),
      timeSlots: this.btsService.getAllBookableTimeSlotsByProjectId(this.id),
    }).subscribe({
      next: ({ agents, timeSlots }) => {

        this.agents = agents
        
        // Prüfe nochmal hasAssessment nach dem Laden der Daten
        if (this.project && this.project.hasAssessment === false) {
          
          // Entferne Assessment-Spalte bevor wir die Agenten zuordnen
          this.statuses = this.statuses.filter((status: any) => status.name !== 'Assessment')
          // WICHTIG: Auch userStatus aktualisieren, da dieser für das Rendering verwendet wird
          this.userStatus = this.userStatus.filter((status: any) => status.name !== 'Assessment')
        }
        
        // Debug-Ausgabe für Assessment-Termine
        console.log('Geladene Agenten mit Assessment-Terminen:', 
          agents.filter(agent => agent.assessmentAppointment).map(agent => ({
            name: `${agent.firstname} ${agent.lastname}`,
            status: agent.status,
            interviewAppointment: agent.interviewAppointment,
            assessmentAppointment: agent.assessmentAppointment
          }))
        );
        
        this.statuses.forEach((status) => {
          status.agents = agents
            .filter(
              (agent) =>
                agent.status === status.name && agent.id !== undefined
            )
            .map((agent: any) => {
              let assessmentAppointment = agent.assessmentAppointment
              let interviewAppointment = agent.interviewAppointment
              
              if (status.name && agent.id) {
                const savedTime = localStorage.getItem(`assessment_time_${agent.id}`)

                if (assessmentAppointment) {
                  console.log(`Lade Assessment-Termin aus agents für ${agent.firstname} ${agent.lastname}: ${assessmentAppointment}`)
                  try {
                    assessmentAppointment = this.getSpecialDateObject(assessmentAppointment)
                  } catch (error) {
                    console.error('Fehler beim Parsen des Assessment-Termins aus agents:', error)
                  }
                } else if (savedTime) {
                  console.log(`Lade Assessment-Termin aus localStorage für ${agent.firstname} ${agent.lastname}: ${savedTime}`)
                  try {
                    assessmentAppointment = this.getSpecialDateObject(savedTime)
                  } catch (error) {
                    console.error('Fehler beim Parsen des Assessment-Termins aus dem localStorage:', error)
                  }
                }
              }

              if (status.name && agent.id) {
                const savedTime = localStorage.getItem(`interview_time_${agent.id}`)

                if (interviewAppointment) {
                  console.log(`Lade Interview-Termin aus agents für ${agent.firstname} ${agent.lastname}: ${interviewAppointment}`)
                  try {
                    interviewAppointment = this.getSpecialDateObject(interviewAppointment)
                  } catch (error) {
                    console.error('Fehler beim Parsen des Interview-Termins aus agents:', error)
                  }
                } else if (savedTime) {
                  console.log(`Lade Interview-Termin aus localStorage für ${agent.firstname} ${agent.lastname}: ${savedTime}`)
                  try {
                    interviewAppointment = this.getSpecialDateObject(savedTime)
                  } catch (error) {
                    console.error('Fehler beim Parsen des Interview-Termins aus dem localStorage:', error)
                  }
                }
              }

              return {
                id: agent.id ?? -1,
                name: `${agent.firstname} ${agent.lastname}`,
                profileImage: agent.agentProfilePhoto && agent.agentProfilePhoto.photoBlob
                  ? this.agentsService.convertImagetoUrl(
                      agent.agentProfilePhoto?.photoBlob
                    )
                  : null,
                status: agent.status,
                city: agent.city,
                country: agent.country,
                videoFiles: agent.videoFiles,
                voiceFiles: agent.voiceFiles,
                interviewAppointment: interviewAppointment,
                assessmentAppointment: assessmentAppointment,
                previousStatus: agent.previousStatus,
                public_id: agent.public_id,
                salutation: agent.salutation,
                agentProfilePhoto: agent.agentProfilePhoto,
                createdAt: agent.createdAt,
                updatedAt: agent.updatedAt,
              }
            });

          if (status.name === 'AgentCreatedFilesMissing') {
            status.agents.unshift({
              id: 0,
              name: 'Agent anlegen',
              status: 'AgentCreatedFilesMissing',
              voiceFiles: [],
              videoFiles: [],
              previousStatus: 'AgentCreatedFilesMissing',
            } as never);
          }
  
          if (status.display === 'Termin gewünscht / vereinbart') {
            this.terminData = this.statuses.find(
              (s) => s.name === 'AppointmentScheduled'
            );
          }
  
          for (const agent of status.agents) {
            this.whatsAppMessagesService.countUnreadWhatsAppMessages(this.project, agent as any)
              .subscribe((count: any) => {
                this.unreadWhatsAppMessagesCount.set(agent.id!, +count);
              });
              
            // Ungelesene interne Nachrichten zählen
            if (agent.id !== 0) { // Nur für echte Agenten, nicht für "Agent anlegen"
              this.messageService.countUnreadMessages((agent as any).public_id)
                .subscribe((count: any) => {
                  this.unreadInternMessagesCount.set(agent.id!, +count);
                });
            }
          }
        });

        this.getUserStatusByRole()
        this.sortAgentsInAllStatuses()
        this.changeDetectorRef.detectChanges()

        this.readWhatsAppMessagesCountSub = this.socket.fromEvent('whatsAppMessagesRead')
          .subscribe((data: any) => {
            this.unreadWhatsAppMessagesCount.set(+data.id, 0);
          });
        this.unreadWhatsAppMessagesCountSub = this.socket.fromEvent('unreadWhatsAppMessagesCount')
          .subscribe((data: any) => {
            this.unreadWhatsAppMessagesCount.set(+data.id, +data.count);
          });
          
        // Subscription für ungelesene interne Nachrichten
        this.unreadInternMessagesCountSub = this.socket.fromEvent('unreadInternMessagesCount')
          .subscribe((data: any) => {
            this.unreadInternMessagesCount.set(+data.id, +data.count);
          });
          
        // Subscription für gelesene interne Nachrichten
        this.socket.fromEvent('internMessagesRead')
          .subscribe((data: any) => {
            this.unreadInternMessagesCount.set(+data.id, 0);
          });
  
        this.getArchiveAgentByReason();
        // this.btsService
      //   .getAllBookableTimeSlotsByProjectId(this.id)
      //   .subscribe((timeSlots: any) => {
      //     this.bookedTimeSlots = timeSlots;
      //     this.getSchedleDurtion(timeSlots);

      //     const scheduledStatus = {
      //       scheduledCount: this.scheduledCount,
      //       terminAgentsCount: this.terminData.agents.length,
      //     };
      //     this.sharedService.scheduledStatusSubject.next(scheduledStatus);
      //     // this.statuses = timeSlots.timeWindows;
      //   });
  
        this.updatescheduledCount();
        this.isProjectDataFetched = true;
        this.getAgentProfilePhoto()
        // Überprüfe, ob Assessment-Termine korrekt geladen wurden
        this.logAssessmentAppointments()
      },
      error: (err : any) => {
        console.error(err);
      }
    });
  }

  private getSpecialDateObject(specialDateString: string): Date {
    if (!specialDateString) {
      return new Date(0);
    }

    // Check if it's an ISO date format (e.g., "2025-08-06T10:00")
    if (specialDateString.includes('T') || specialDateString.includes('-')) {
      try {
        return new Date(specialDateString)
      } catch (error) {
        console.error('Fehler beim Parsen des ISO-Datums:', specialDateString, error)
        return new Date(0)
      }
    }

    // Parsen des gespeicherten Datums (Format: dd.MM.yyyy HH:mm)
    const parts = specialDateString.split(' ')
    if (!parts || parts.length < 2) {
      console.error('Ungültiges Datumsformat:', specialDateString)
      return new Date(0)
    }

    const dateParts = parts[0]?.split('.')
    const timeParts = parts[1]?.split(':')

    if (!dateParts || dateParts.length < 3 || !timeParts || timeParts.length < 2) {
      console.error('Ungültiges Datums- oder Zeitformat:', specialDateString)
      return new Date(0)
    }
                    
    const day = parseInt(dateParts[0], 10)
    const month = parseInt(dateParts[1], 10) - 1 // Monate sind 0-basiert in JavaScript
    const year = parseInt(dateParts[2], 10)
    const hour = parseInt(timeParts[0], 10)
    const minute = parseInt(timeParts[1], 10)

    return new Date(year, month, day, hour, minute)
  }

  private lastEvent: any;

  public onDropFromActive(event: any): void {
    this.lastEvent = event;

    if (!this.dialogService.performDropAction) {
      return;
    }

    if (event.data.id != 0) {
      this.doDrop(event);
    }
  }

  public doDrop(event: any): void {
    const data: any = event.data;
    const fromStatus = this.userStatus.find((s) => s.name === event.from);
    const toStatus: any = this.userStatus.find((s) => s.name === event.to);
    if (fromStatus && toStatus) {
      const agentIndex = fromStatus.agents.findIndex(
        (agent) => agent.id === data.id
      );
      if (agentIndex !== -1) {
        const [removedAgent] = fromStatus.agents.splice(agentIndex, 1);
        removedAgent.status = toStatus.name;
        removedAgent.previousStatus = fromStatus.name;
        toStatus.agents.push(removedAgent);
        // message log
        this.logAgentMoment(removedAgent);
      // keep column order by entry time
      this.sortAgentsInAllStatuses();
      }

      const agentStatus = {
        AgentStatus: toStatus.name,
      };
      this.agentsService
        .updateAgentStatus(data.id, agentStatus)
        .subscribe((res: any) => {
          // ToDo: If Any post request code.
          // if(event.from=='ApplicantAccepted' && event.to=='ApplicantRejected' && this.currentRole==='Admin'){
          //   this.archivAgent(data.id,'ApplicantArchived');
          // }
        });
    }
    if (toStatus?.display == 'Termin gewünscht / vereinbart' || fromStatus?.display == 'Termin gewünscht / vereinbart') {
      this.terminData = this.userStatus.find(
        (status) => status.name === 'AppointmentScheduled'
      );
      this.getSchedleDurtion(this.bookedTimeSlots);

      const scheduledStatus = {
        scheduledCount: this.scheduledCount,
        terminAgentsCount: this.terminData.agents.length,
      };
      this.sharedService.scheduledStatusSubject.next(scheduledStatus);
    }
    if (toStatus?.name == 'AppointmentScheduled' && this.project.hasVgSlots) {
      this.agentId = data.id;
      let totalSlotDuration = 0;
      this.bookedTimeSlots.forEach((slot: any) => {
        totalSlotDuration += slot.slotDuration;
      });
      const minimumRequiredTime = this.terminData.agents.length * 30;

      if (totalSlotDuration < minimumRequiredTime) {
        this.showTimeSlotDialog();
      }
    }
  }

  public moveAgentWithoutVoicefile() {
    this.doDrop(this.lastEvent);

    this.dialogService.showMoveAgentWithoutVoicefileDialog = false;
    this.dialogService.performDropAction = true;
  }

  public displayToastForAgentWithoutVoicefile() {
    const message = 'Der Agent wurde nicht verschoben.';

    this.snackBar.open(message, '', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });

    this.dialogService.showMoveAgentWithoutVoicefileDialog = false;
    this.dialogService.performDropAction = true;
  }

  navigateToSingleView(id: number, status: any) {
    this.data = {
      agentId: id,
      projectId: this.id,
      coloumnName: status,
    };
    this.dialogService.showAgentView = true;
  }

  showTimeSlotDialog() {
    this.dialogService.showTimeSlotDialog = true;
  }

  showCreateAgentDialog() {
    this.dialogService.showCreateAgentDialog = true;
  }

  getSchedleDurtion(timeSlots: any[]) {
    let slotCount = 0;
    timeSlots.forEach((data) => {
      const today = new Date().setHours(0, 0, 0, 0);
      Object.keys(data.timeWindows).forEach((dayName) => {
        const daySlots = data.timeWindows[dayName];
        const dayDates = this.getDateFromDayName(
          data.fromDate,
          data.toDate,
          dayName
        );
        if (daySlots.length > 0) {
          dayDates.forEach((date: Date) => {
            if (date.getTime() > today) {
              slotCount += daySlots.length;
            }
          });
        }
      });
    });
    this.scheduledCount = slotCount;
    const scheduledStatus = {
      scheduledCount: this.scheduledCount,
      terminAgentsCount: this.terminData?.agents.length ?? 0,
    };
    this.sharedService.scheduledStatusSubject.next(scheduledStatus);
  }

  updatescheduledCount() {
    this.btsService
    .getAllBookableTimeSlotsByProjectId(this.id)
    .subscribe((timeSlots: any) => {
      this.bookedTimeSlots = timeSlots;
      this.getSchedleDurtion(timeSlots);

      const scheduledStatus = {
        scheduledCount: this.scheduledCount,
        terminAgentsCount: this.terminData?.agents.length ?? 0,
      };
      this.sharedService.scheduledStatusSubject.next(scheduledStatus);
      // this.statuses = timeSlots.timeWindows;
    });
    // Aktualisiere originalAgents, damit die Filterung korrekt funktioniert
    if (this.originalAgents) {
      this.originalAgents = this.userStatus.map(status => [...status.agents]);
    }
    this.search.setValue('', { emitEvent: true });
    
    this.changeDetectorRef.detectChanges();
  }

  scheduledDurationChanged(event: any) {
    const index = this.bookedTimeSlots.findIndex((timeSlot: any) => {
      return (
        moment(event.fromDate).isSame(moment(timeSlot.fromDate)) &&
        moment(event.toDate).isSame(moment(timeSlot.toDate))
      );
    });
    if (index >= 0) {
      this.bookedTimeSlots[index] = event;
    } else {
      this.bookedTimeSlots.push(event);
    }
    this.getSchedleDurtion(this.bookedTimeSlots);
  }

  public mediaVoiceFiles: any[] = [];
  public mediaVideoFiles: any[] = [];

  public async openMediaModal(event: Event, id: number) {
    event.stopPropagation();
    
      if (this.lastOpenedAgentId === id) {
        return; 
      }
      this.lastOpenedAgentId =id;
    this.isSpinnerActive = true;
    const agent: any = this.agents.find((agent) => agent.id === id);

    await this.getVoiceFilesForAgent(agent);
    await this.getVideoFilesForAgent(agent);

    const hasVoiceFiles = this.mediaVoiceFiles && this.mediaVoiceFiles.length > 0;
    const hasVideoFiles = this.mediaVideoFiles && this.mediaVideoFiles.length > 0;
    if (!hasVoiceFiles && !hasVideoFiles) {
      this.snackBar.open('Nicht akzeptierte Audiodateien oder Videodateien.', '', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
      this.isSpinnerActive = false;
    } else {
      this.isMediaPlay = true;
      this.isSpinnerActive = false;
    }
  }

  public async getVoiceFilesForAgent(agent: any) {
    this.mediaVoiceFiles = [];
    const voiceBlobs = agent?.voiceFiles.filter((voice: any) => voice.status === 'accepted');
    if (voiceBlobs?.length) {
      for (const voiceBlob of voiceBlobs) {
        const url = this.agentsService.getAudioStreamUrl(voiceBlob.id);
        this.mediaVoiceFiles.push(url); // Push the streaming URL directly
      }
    }
  }
  
  public async getVideoFilesForAgent(agent: any) {
    this.mediaVideoFiles = [];
    const videoBlobs = agent?.videoFiles.filter((video: any) => video.status === 'accepted');
    if (videoBlobs?.length) {
      for (const videoBlob of videoBlobs) {
        const url = this.agentsService.getVideoStreamUrl(videoBlob.id);
        this.mediaVideoFiles.push(url); // Push the streaming URL directly
      }
    }
  }
  

  adminSelected(): void {
    this.currentRole = 'Admin';
    this.getUserStatusByRole();
  }

  clientSelected(): void {
    this.currentRole = 'Client';
    this.getUserStatusByRole();
  }

  getUserStatusByRole() {
    
    if (this.currentRole != 'Admin') {
      this.userStatus = this.statuses.filter((item: any) =>
        item.roles.includes(this.currentRole)
      );
    } else {
      this.userStatus = [...this.statuses];
    }
  }

  isStatusIsOpenTask(status: string): boolean {
    return (
      status === 'ApplicantGetsChecked' ||
      status === 'ApplicantGetsSuggested' ||
      status === 'AppointmentScheduled' ||
      status === 'ApplicantAccepted'
    );
  }

  isFilesMissing(videoFiles: any, voiceFiles: any) {
    return videoFiles?.length === 0 && voiceFiles?.length === 0;
  }

  onAgentStatusChange(event: any) {
    console.log('Agent Status Change Event erhalten:', event);
    
    // Finde den Agenten im lokalen Array
    let agent: any = this.agents.find((agent) => {
      return agent.id === event.id;
    });
    
    // Finde die Status-Objekte
    const fromStatus: any = this.userStatus.find(
      (s) => s.name === event.previousStatus
    );
    const toStatus: any = this.userStatus.find((s) => s.name === event.status);
    
    if (!fromStatus || !toStatus) {
      console.error('Status nicht gefunden:', { from: event.previousStatus, to: event.status });
      return;
    }
    
    console.log(`Verschiebe Agent von ${fromStatus.name} nach ${toStatus.name}`);
    
    // Finde den Agenten im fromStatus-Array
    const agentIndex = fromStatus.agents.findIndex(
      (agent: any) => agent.id === event.id
    );

    // Verschiebe den Agenten von einem Status zum anderen
    if (agentIndex !== -1) {
      // Entferne den Agenten aus dem alten Status
      const [removedAgent] = fromStatus.agents.splice(agentIndex, 1);
      
      // Aktualisiere die Eigenschaften des Agenten
      const updatedAgent = {
        ...removedAgent,
        previousStatus: fromStatus.name,
        status: toStatus.name,
        name: event.name || removedAgent.name,
        voiceFiles: event.voiceFiles || removedAgent.voiceFiles || [],
        videoFiles: event.videoFiles || removedAgent.videoFiles || [],
        createdAt: removedAgent.createdAt,
        updatedAt: event.updatedAt || removedAgent.updatedAt
      };
      
      // Füge den aktualisierten Agenten zum neuen Status hinzu
      toStatus.agents.push(updatedAgent);
      this.logAgentMoment(updatedAgent);
  // keep column order by entry time
  this.sortAgentsInAllStatuses();
    } else {
      console.warn(`Agent mit ID ${event.id} wurde im Status ${fromStatus.name} nicht gefunden`);
      
      // Versuche, den Agenten in allen Status-Arrays zu finden und zu verschieben
      let foundInOtherStatus = false;
      
      for (const status of this.userStatus) {
        if (status.name === toStatus.name) continue; // Zielstatus überspringen
        
        const idx = status.agents.findIndex((a: any) => a.id === event.id);
        if (idx !== -1) {
          console.log(`Agent wurde in Status ${status.name} gefunden und wird verschoben`);
          const [agent] = status.agents.splice(idx, 1);
          agent.previousStatus = status.name;
          agent.status = toStatus.name;
          toStatus.agents.push(agent);
          foundInOtherStatus = true;
          break;
        }
      }
      
      // Falls der Agent nirgendwo gefunden wurde, erstelle einen neuen Eintrag
      if (!foundInOtherStatus) {
        console.log('Agent wurde in keinem Status gefunden, erstelle neuen Eintrag');
        const newAgent = {
          id: event.id,
          firstname: event.firstname || '',
          lastname: event.lastname || '',
          status: event.status,
          previousStatus: event.previousStatus,
          voiceFiles: event.voiceFiles || [],
          videoFiles: event.videoFiles || []
        };
        toStatus.agents.push(newAgent);
      }
    }
    
    // Aktualisiere den Agenten im Haupt-Array
    if (agent) {
      agent.status = event.status;
      if (event.firstname) agent.firstname = event.firstname
      if (event.lastname) agent.lastname = event.lastname
    }
    else {
      // Verwende 'as any', da wir hier ein vereinfachtes Agenten-Modell verwenden
      this.agents.push({
        id: event.id,
        firstname: event.firstname || '',
        lastname: event.lastname || '',
        status: event.status,
        previousStatus: event.previousStatus,
        voiceFiles: event.voiceFiles || [],
        videoFiles: event.videoFiles || []
      } as any);
    }
    
    // Sort agents in every status before change detection
    this.sortAgentsInAllStatuses();
    // Erzwinge die Aktualisierung der Ansicht
    this.changeDetectorRef.detectChanges();
    
    // Zusätzlich nach einer kurzen Verzögerung nochmals aktualisieren
    // Dies hilft bei Rendering-Problemen in Angular
    setTimeout(() => {
      // Aktualisiere originalAgents, damit die Filterung korrekt funktioniert
    if (this.originalAgents) {
      this.originalAgents = this.userStatus.map(status => [...status.agents]);
    }
    this.search.setValue('', { emitEvent: true });
    
    this.changeDetectorRef.detectChanges();
    }, 100);
  }

  onselectInterviewDate(event: any) {
    let agent: any = this.agents.find((agent) => {
      return agent.id === event.id
    })
    if(agent){
      agent.interviewAppointment = event.interviewAppointment
    }
    const status:any = this.userStatus.find((s) => s.name === event.status)
    const agentIndex = status.agents.findIndex(
      (agent: any) => agent.id === event.id
    )
    if (agentIndex !== -1) {
      status.agents[agentIndex].interviewAppointment = event.interviewAppointment
    }
  }
  
  // Diese Methode wird aufgerufen, wenn der Assessment-Termin in der AgentView geändert wurde
  handleAssessmentDateChange(event: any) {
    console.log('Assessment-Termin aktualisiert:', event)
    
    // Aktualisiere den Assessment-Termin im agents-Array
    let agent: any = this.agents.find((agent) => {
      return agent.id === event.agentId
    })
    if(agent) {
      agent.assessmentAppointment = event.assessmentAppointment
    }
    
    // Finde die Assessment-Spalte
    const status = this.userStatus.find((s) => s.name === 'Assessment')
    if (status) {
      // Suche den Agent in der Assessment-Spalte
      const agentIndex = status.agents.findIndex(
        (agent: any) => agent.id === event.agentId
      )
      if (agentIndex !== -1) {
        // Aktualisiere den Assessment-Termin
        // Type-Cast zu 'any' um TypeScript-Fehler zu vermeiden
        (status.agents[agentIndex] as any).assessmentAppointment = event.assessmentAppointment
        
        // Erzwinge ein Change Detection, damit die Änderung sofort angezeigt wird
        this.changeDetectorRef.detectChanges()
      }
    }
  }

  getPartnerInfo(partnerId: any) {
    this.agentsService.getPartnerInfo(partnerId).subscribe((res) => {
      if (!this.project) {
        console.warn('getPartnerInfo: project is null, skipping partnerInfo assignment', {
          partnerId,
          projectId: this.id
        })
        return
      }
      this.project.partnerInfo = res
    })
  }
  onMoveAgent() {
    this.ngOnInit();
  }

  onClickProjectDesc() {
    this.dialogService.showNewProjectDialog = true;
  }

  /**
   * Diese Methode wird aufgerufen, wenn das Projekt im NewProject-Dialog aktualisiert wurde
   * Sie lädt die Projektdaten neu, um Änderungen wie das Assessment-Flag sofort anzuzeigen
   */
  onProjectUpdated(updatedProject: any) {
    console.log('Projekt wurde aktualisiert:', updatedProject);
    // Aktualisiere das lokale Projekt-Objekt mit den neuen Daten
    this.project = { ...this.project, ...updatedProject };
    
    // Lade die Projektdaten neu, um die UI zu aktualisieren (z.B. Assessment-Spalte anzeigen/ausblenden)
    this.loadProjectData();
  }
  
  public getBorderColor(entry: any): string {
  let color = '';
  switch (entry.status) {
    case 'AgentCreatedFilesMissing':
      color = 'border-green';
      break;
    case 'ApplicantGetsChecked':
      color = 'border-green';
      break;
    case 'WaitingForAgent':
      if (entry.voiceFiles.length == 0 && entry.videoFiles.length == 0) {
        color = 'border-grey';
      } else if (
        entry.voiceFiles.some((obj: any) => obj.status === 'accepted') ||
        entry.videoFiles.some((obj: any) => obj.status === 'accepted')
      ) {
        color = 'border-green';
      } else {
          color = 'border-red';
      }
        // else if (
        //   entry.voiceFiles.some((obj: any) => obj.isPlayed === false)
        // ) {
        //   color = 'border-red';
        // } else {
      //   color = 'border-green';
      // }
      break;
    case 'ApplicantGetsSuggested':
      color = 'border-green';
      break;
    case 'ApplicantRejected':
      color = 'border-green';
      break;
    case 'AppointmentScheduled':
      if (entry.interviewAppointment) {
        color = 'border-green';
      } else {
        color = 'border-yellow';
      }
      break;
    case 'Assessment':
      if (entry.assessmentAppointment) {
        color = 'border-green';
      } else {
        color = 'border-yellow';
      }
      break;
    case 'ApplicantAccepted':
      color = 'border-green';
      break;
    case 'ApplicantConfirmed':
      color = 'border-green';
      break;

    default:
      break;
  }
  return color;
} 
  
  // Diese Methode hilft, Assessment-Termine im Kanban-Board zu debuggen
  public logAssessmentAppointments(): void {
    this.userStatus.forEach(status => {
      if (status.name === 'Assessment') {
        console.log('Assessment-Spalte enthält folgende Agenten:', status.agents.map((agent: any) => ({
          name: agent.name,
          assessmentAppointment: agent.assessmentAppointment
        })));
      }
    });
  }

  isPlayIconVisible(entry: any) {
    switch (entry.status) {
      case 'WaitingForAgent':
      case 'ApplicantGetsSuggested':
      case 'ApplicantRejected':
      case 'AppointmentScheduled':
      case 'Assessment':
      case 'ApplicantAccepted':
      case 'ApplicantConfirmed':
      case 'ApplicantArchived':
        return (entry.voiceFiles?.length || 0) > 0 || (entry.videoFiles?.length || 0) > 0;
      default:
        return false;
    }
  }

  isMessageIconVisible(entry: Agent) {
    const count = this.unreadWhatsAppMessagesCount.has(entry.id!) ? this.unreadWhatsAppMessagesCount.get(entry.id!)! : 0;
    return count > 0;
  }

  isInternMessageIconVisible(entry: Agent) {
    const count = this.unreadInternMessagesCount.has(entry.id!) ? this.unreadInternMessagesCount.get(entry.id!)! : 0;
    return count > 0;
  }

  getPlayIconColor(entry: any) {
    if (entry.voiceFiles.some((obj: any) => obj.status === 'accepted') || entry.videoFiles.some((obj: any) => obj.status === 'accepted')) {
      return '#24b89b';
    } else if (
      entry.voiceFiles.every((obj: any) => obj.status !== 'accepted')
    ) {
      return '#af1c3d';
    } else {
      return '#6c6c6c';
    }
    // if (entry.voiceFiles.some((obj: any) => obj.isPlayed === false)) {
    //   return '#af1c3d';
    // } else if (entry.voiceFiles.every((obj: any) => obj.isPlayed === true)) {
    //   return '#24b89b';
    // } else {
    //   return '#6c6c6c';
    // }
  }

  agentVoiceFileStatusChange() {
    this.subscription = this.sharedService.agentVoiceFile$.subscribe(
      (value: any) => {
        const fromStatus = this.userStatus.find((s) => s.name === value.status);
        if (fromStatus) {
          const agentIndex = fromStatus.agents.findIndex(
            (agent) => agent.id === value.id
          );
          if (value.isVoiceFile) {
            fromStatus.agents[agentIndex].voiceFiles?.forEach((element: any) => {
              if (element.id == value.fileStatusChangeId) {
                element.isPlayed = true;
              }
            });
          } else {
            fromStatus.agents[agentIndex].videoFiles?.forEach((element: any) => {
              if (element.id == value.fileStatusChangeId) {
                element.isPlayed = true;
              }
            });
          }
        }
      }
    );
  }

  getArchiveAgentByReason() {
    const reasons = [
      'rejected by customer for project',
      'Agent canceled or cannot keep an appointment',
    ];
    this.agentsService
      .getArchiveAgentByReasons(reasons, this.id, this.partnerId)
      .subscribe((res: any) => {
        this.sharedService.negativeAgentsFetchedSubject.next(true);
        if (res.length) {
          const ArryOfAgents: any = this.statuses.find(
            (status) => status.name == 'ApplicantRejected'
          )?.agents;
          res.forEach((agent: any) => {
            const agentObj = {
              id: agent.id ?? -1,
              name: `${agent.firstname} ${agent.lastname}`,
              profileImage: agent.agentProfilePhoto
                ? this.agentsService.convertImagetoUrl(
                    agent.agentProfilePhoto?.photoBlob ?? ''
                  )
                : null,
              status: agent.status,
              city: agent.city,
              country: agent.country,
              videoFiles: agent.videoFiles,
              voiceFiles: agent.voiceFiles,
              interviewAppointment: agent.interviewAppointment,
              salutation: agent.salutation,
            };
            ArryOfAgents?.push(agentObj);
          });
        }
      });
  }
  archivAgent(agentId: any, AgentStatus: string) {
    const obj = {
      reason: 'Agent canceled or cannot keep an appointment',
      AgentStatus: AgentStatus,
    };
    this.agentsService.archiveAgent(Number(agentId), obj).subscribe(() => {});
  }

  getDateFromDayName(fromDate: any, toDate: any, dayName: any) {
    const dayIndex: any = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };

    const from = new Date(fromDate);
    const to = new Date(toDate);

    const resultDates = [];

    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === dayIndex[dayName]) {
        resultDates.push(new Date(d));
      }
    }

    return resultDates;
  }

  onFileaccepted(event: any) {
    const agentStatus: any = this.statuses.find((s) => s.name === event.status);
    const agentIndex = agentStatus.agents.findIndex(
      (agent: any) => agent.id === event.agentId
    );
    if (event.isAudioFile) {
      const audioFileIndex = agentStatus.agents[
        agentIndex
      ].voiceFiles.findIndex((audio: any) => audio.id === event.fileId);
      agentStatus.agents[agentIndex].voiceFiles[audioFileIndex].status =
        'accepted';
    } else {
      const audioFileIndex = agentStatus.agents[
        agentIndex
      ].videoFiles.findIndex((audio: any) => audio.id === event.fileId);
      agentStatus.agents[agentIndex].videoFiles[audioFileIndex].status =
        'accepted';
    }
  }

  onFilerejected(event: any) {
    const agentStatus: any = this.statuses.find((s) => s.name === event.status);
    const agentIndex = agentStatus.agents.findIndex(
      (agent: any) => agent.id === event.agentId
    );
    if (event.isAudioFile) {
      const audioFileIndex = agentStatus.agents[
        agentIndex
      ].voiceFiles.findIndex((audio: any) => audio.id === event.fileId);
      agentStatus.agents[agentIndex].voiceFiles[audioFileIndex].status =
        'rejected';
    } else {
      const audioFileIndex = agentStatus.agents[
        agentIndex
      ].videoFiles.findIndex((audio: any) => audio.id === event.fileId);
      agentStatus.agents[agentIndex].videoFiles[audioFileIndex].status =
        'rejected';
    }
  }

  logAgentMoment(agent: any) {
    let text = `Verschoben von ${
      this.sharedService.statuses[agent.previousStatus].display
    } zu ${this.sharedService.statuses[agent.status].display}`
    this.sharedService.logAgentMoment(
      { ...agent, project: { id: this.project.id } },
      this.currentUser,
      this.currentRole,
      text,
      this.currentUserName
    )
  }

  handleCloseView(updatedAgent?: any) {
    if (!updatedAgent) {
      return;
    }

    const { isDeleted } = updatedAgent;

  if (isDeleted) {
    this.agents = this.agents.filter((a) => a.id !== updatedAgent.id);

    this.statuses.forEach((status) => {
      status.agents = status.agents.filter(
        (existingAgent: any) => existingAgent.id !== updatedAgent.id
      );
    });

    this.changeDetectorRef.detectChanges();
    return;
  }
  
    const formattedAgent = {
      id: updatedAgent.id ?? -1,
      name: `${updatedAgent.firstname} ${updatedAgent.lastname}`,
      status: updatedAgent.status ?? 'Unknown',
      previousStatus: updatedAgent.previousStatus ?? 'Unknown',
      voiceFiles: updatedAgent.voiceFiles ?? [],
      videoFiles: updatedAgent.videoFiles ?? [],
      city: updatedAgent.city ?? '',
      country: updatedAgent.country ?? '',
      interviewAppointment: updatedAgent.interviewAppointment ?? null,
      assessmentAppointment: updatedAgent.assessmentAppointment ?? null,
      public_id: updatedAgent.public_id ?? '',
      salutation: updatedAgent.salutation ?? '',
      respondedOnWhatsApp: updatedAgent.respondedOnWhatsApp ?? false,
      b2bCapable: updatedAgent.b2bCapable ?? false,
      germanLevel: updatedAgent.germanLevel ?? '',
      englishLevel: updatedAgent.englishLevel ?? '',
      outboundExperienceLevel: updatedAgent.outboundExperienceLevel ?? '',
      profileImage: updatedAgent.agentProfilePhoto?.photoBlob
        ? this.agentsService.convertImagetoUrl(updatedAgent.agentProfilePhoto.photoBlob)
        : null, // Convert blob to URL
    };
  
    let oldStatus = this.statuses.find(status =>
      status.agents.some(agent => agent.id === updatedAgent.id)
    );
  
    if (oldStatus) {
      const agentIndex = oldStatus.agents.findIndex(agent => agent.id === updatedAgent.id);
      if (agentIndex !== -1) {
        oldStatus.agents[agentIndex] = { ...oldStatus.agents[agentIndex], ...formattedAgent };
      }
    }
  
    if (oldStatus && oldStatus.name !== updatedAgent.status && (oldStatus.name !== AgentStatus.ApplicantRejected && updatedAgent.status !== AgentStatus.ApplicantArchived)) {
      oldStatus.agents = oldStatus.agents.filter(agent => agent.id !== updatedAgent.id);
  
      const newStatus = this.statuses.find(status => status.name === updatedAgent.status);
      if (newStatus) {
        const existingAgentIndex = newStatus.agents.findIndex(agent => agent.id === updatedAgent.id);
        if (existingAgentIndex !== -1) {
          newStatus.agents[existingAgentIndex] = { ...newStatus.agents[existingAgentIndex], ...formattedAgent };
        } else {
          newStatus.agents.push(formattedAgent as never);
        }
      }
    }
  
    // Aktualisiere originalAgents, damit die Filterung korrekt funktioniert
    if (this.originalAgents) {
      this.originalAgents = this.statuses.map(status => [...status.agents]);
    }
    this.search.setValue('', { emitEvent: true });
    
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Lädt Profilbilder für alle Agenten im Kanban-Board
   */
  public getAgentProfilePhoto() {
    this.userStatus.forEach((status) => {
      status.agents.forEach((agent: any) => {
        if (agent.agentProfilePhoto?.id) {
          this.agentsService.getAgentProfilePhoto(agent.agentProfilePhoto.id).subscribe({
            next: (photo:any) => {
              agent.profileImage = this.agentsService.convertImagetoUrl(photo.photoBlob);
              // this.changeDetectorRef.detectChanges();
            },
            error: (err) => {
              console.error(`Failed to load photo for agent ${agent.id}`, err);
            }
          });
        }
      });
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.readWhatsAppMessagesCountSub?.unsubscribe();
    this.unreadWhatsAppMessagesCountSub?.unsubscribe();
    this.unreadInternMessagesCountSub?.unsubscribe();
  }

  adjustImage(image: HTMLImageElement): void {
    if (image.naturalWidth === image.naturalHeight) {
      image.style.width = '100%';
      image.style.height = '100%';
    } else if (image.naturalWidth < image.naturalHeight) {
      image.style.width = '100%';
      image.style.height = 'auto';
    } else {
      image.style.width = 'auto';
      image.style.height = '100%';
    }
  }
}
